import {Table} from 'src/design/atoms/Table';
import {Anchor} from 'src/design/atoms/Anchor';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconMessage} from '@tabler/icons-react';
import React from 'react';
import type {DocumentReviewRoundResponse} from 'src/api/ProactApiClient';
import {DocumentReviewTypeEnum} from 'src/api/ProactApiClient';
import {CompletionProgress} from 'src/design/atoms/CompletionProgress';
import {Pill} from 'src/design/atoms/Pill';
import {RefreshButton} from 'src/design/molecules/RefreshButton';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {
  DocumentReviewRoundStatePill,
  DocumentReviewTypeLabel,
  formatReviewDate,
} from '../../documentReviewHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface DocumentReviewsTabProps {
  rounds: DocumentReviewRoundResponse[] | undefined;
  isLoading: boolean;
  isFetching?: boolean;
  onRefresh: () => void;
}

/**
 * Review round history for a document across all versions — every Approval
 * and Acknowledgement round, most recent first.
 */
export const DocumentReviewsTab: React.FC<DocumentReviewsTabProps> = ({
  rounds,
  isLoading,
  isFetching,
  onRefresh,
}) => {
  const sortedRounds = [...(rounds ?? [])].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        <RefreshButton onClick={onRefresh} loading={isFetching} />
      </Group>
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : sortedRounds.length === 0 ? (
        <Text size="sm" c="dimmed" p="md" data-testid="document-reviews-tab-empty">
          No review rounds for this document.
        </Text>
      ) : (
        <ReviewRoundsTable rounds={sortedRounds} />
      )}
    </Stack>
  );
};

interface ReviewRoundsTableProps {
  rounds: DocumentReviewRoundResponse[];
}

const ReviewRoundsTable: React.FC<ReviewRoundsTableProps> = ({rounds}) => {
  const navigate = useAppNavigate();

  return (
    <Table data-testid="document-reviews-tab">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Type</Table.Th>
          <Table.Th>Version</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Progress</Table.Th>
          <Table.Th>Requested</Table.Th>
          <Table.Th>Resolved</Table.Th>
          <Table.Th>Task</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rounds.map(round => {
          const stats = round.participationStatistics;
          return (
            <Table.Tr key={round.id}>
              <Table.Td>{DocumentReviewTypeLabel(round.reviewType)}</Table.Td>
              <Table.Td>{round.documentVersion}</Table.Td>
              <Table.Td>
                <Pill {...DocumentReviewRoundStatePill(round.state)} />
              </Table.Td>
              <Table.Td>
                {round.reviewType === DocumentReviewTypeEnum.Acknowledgment ? (
                  <CompletionProgress
                    completed={stats?.completed ?? 0}
                    total={stats?.total ?? 0}
                  />
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm">{round.requestedByName ?? '—'}</Text>
                <Text size="xs" c="dimmed">
                  {formatReviewDate(round.createdAt)}
                </Text>
              </Table.Td>
              <Table.Td>
                {round.resolvedByName ? (
                  <>
                    <Group gap={4} wrap="nowrap">
                      <Text size="sm">{round.resolvedByName}</Text>
                      {round.resolutionComment && (
                        <Tooltip label={round.resolutionComment} multiline w={260}>
                          <IconMessage size={14} />
                        </Tooltip>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {formatReviewDate(round.resolvedAt)}
                    </Text>
                  </>
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                {round.taskDefinitionId ? (
                  <Anchor
                    size="sm"
                    onClick={() =>
                      navigate(
                        PathUtility.TaskView(
                          round.taskType,
                          round.taskDefinitionId!,
                          'content'
                        )
                      )
                    }>
                    View task
                  </Anchor>
                ) : (
                  '—'
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};
